import { useLoaderData } from "react-router-dom";
import { JFT, LoaderData, Version, prettyPrintVersion } from "./types";
import { Box, Chip, Grid, Paper, Typography, css } from "@mui/material";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import { useContext } from "react";
import { AppContext } from "./app-context";
import { trace } from "./util";

export interface BodyProps {
  jft: JFT;
}

export function Body() {
  const { jfts } = useLoaderData() as LoaderData;
  const jftsByVersion = jftArrayToObject(jfts);

  const { showOriginal, showNew, selectedVersions, showTwoColumns } =
    useContext(AppContext);

  return (
    <Box position="relative" overflow="auto" flexGrow={1}>
      <Box p={4}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            mb: 2,
          }}
        >
          {jftsByVersion["original"].title}
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="body2">
            {jftsByVersion["original"].excerpt}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              alignSelf: "flex-end",
            }}
          >
            {jftsByVersion["original"].excerpt_source &&
              `— ${jftsByVersion["original"].excerpt_source}`}
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={2} columns={2} p={2}>
        {showOriginal && (
          <Grid item xs={2} sm={showTwoColumns && showNew ? 1 : 2}>
            <JftPanel jft={jftsByVersion["original"]} />
          </Grid>
        )}
        {showNew &&
          selectedVersions.map((version) => (
            <Grid
              key={version}
              item
              xs={2}
              sm={
                showTwoColumns && (showOriginal || selectedVersions.length > 1)
                  ? 1
                  : 2
              }
            >
              <JftPanel jft={jftsByVersion[version]} />
            </Grid>
          ))}
      </Grid>
    </Box>
  );
}

interface JftPanelProps {
  jft: JFT;
}

function JftPanel({ jft }: JftPanelProps) {
  const { body, jft_principle } = jft;
  return (
    <Paper
      elevation={3}
      sx={{
        padding: 4,
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="0.5rem"
      >
        <Box display="flex" justifyContent="flex-end" alignSelf="stretch">
          <Chip label={prettyPrintVersion(jft.version)} variant="filled" />
        </Box>
        {jft.audio_file && (
          <audio
            style={{
              width: "100%",
            }}
            controls
            src={`/audio/${jft.audio_file}`}
          />
        )}
      </Box>
      <Typography component="div" variant="body1">
        <ReactMarkdown>{body.replaceAll("\n", "  \n")}</ReactMarkdown>
        {jft_principle && (
          <p>
            <strong>Just for today: </strong>{" "}
            {jft_principle?.replace(/^just for today: /i, "")}
          </p>
        )}
      </Typography>
    </Paper>
  );
}

type VersionMap = {
  [version in Version]: JFT;
};

/** Convert an array of JFTs into an object keyed by version and the values are the JFT. */
export function jftArrayToObject(jfts: JFT[]) {
  const jftMap: { [version: string]: JFT } = {};
  for (const jft of jfts) {
    jftMap[jft.version] = jft;
  }
  return jftMap as VersionMap;
}
